import { useEffect, useMemo, useState, useRef } from 'react';
import ApexChart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { Container } from '@/components/container';
import { DataGrid } from '@/components';
import { usePermissions } from '@/hooks/usePermissions';
import {
  useGetSubscriptionAdoptionReportQuery,
  useLazyGetSubscriptionAdoptionCsvQuery,
} from '@/services/HistoricalTrendsService';
import {
  SubscriptionActiveSnapshotItem,
  SubscriptionHistoryItem,
} from '@/types/historical-trends.types';
import { GroupByUnit, SubscriptionType } from '@/constant';
import Select, { MultiValue } from 'react-select';
import { SubscriptionsService } from '@/services/subscriptions/subscriptionsService';
import { generateReportPdf } from '@/utils/pdfExport';
import { useSnackbar } from 'notistack';
import type { PaginationState } from '@tanstack/react-table';
import DateRangePicker from '@/components/inputControls/DateRangePicker';
import moment from 'moment';
import { formatDisplayDate } from '@/utils/Date';

const stripHtml = (input: string) => input.replace(/<[^>]*>/g, '').trim();

type SelectOption = { value: string; label: string };

const subscriptionTypeLabel = (val?: string | number) => {
  const n = typeof val === 'string' ? Number(val) : val;
  switch (n) {
    case SubscriptionType.BOATER:
      return 'Boater';
    case SubscriptionType.HARBOR:
      return 'Harbor';
    case SubscriptionType.CLUB:
      return 'Club';
    case SubscriptionType.DMO:
      return 'DMO';
    case SubscriptionType.SERVICE_PROVIDER:
      return 'Service Provider';
    default:
      return 'Unknown';
  }
};

const HistoricalTrends = () => {
  const { hasPermission } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();
  const chartRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const groupByOptions: SelectOption[] = useMemo(
    () => [
      { value: GroupByUnit.DAY, label: 'Daily' },
      { value: GroupByUnit.WEEK, label: 'Weekly' },
      { value: GroupByUnit.MONTH, label: 'Monthly' },
    ],
    []
  );
  const [selectedGroupBy, setSelectedGroupBy] = useState<SelectOption | null>(
    groupByOptions[2]
  );
  const [subscriptionOptions, setSubscriptionOptions] = useState<
    SelectOption[]
  >([]);
  const [selectedSub, setSelectedSub] = useState<SelectOption | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 25,
  });
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  const subscriptionTypeOptions: SelectOption[] = [
    { value: String(SubscriptionType.BOATER), label: 'Boater' },
    { value: String(SubscriptionType.HARBOR), label: 'Harbor' },
    { value: String(SubscriptionType.CLUB), label: 'Club' },
    { value: String(SubscriptionType.DMO), label: 'DMO' },
    {
      value: String(SubscriptionType.SERVICE_PROVIDER),
      label: 'Service Provider',
    },
  ];

  const handleExportPdf = async () => {
    try {
      setPdfLoading(true);
      const date = new Date().toISOString().slice(0, 10);

      await generateReportPdf({
        filename: `historical_trends_${
          (selectedGroupBy?.value as GroupByUnit) || GroupByUnit.MONTH
        }_${date}.pdf`,
        title: 'Historical Growth Trends Report',
        subtitle: `Grouped by ${selectedGroupBy?.label || 'Month'}${
          selectedSub ? ` - ${selectedSub.label}` : ''
        }`,
        chartElement: chartRef.current || undefined,
        tableElement: tableRef.current || undefined,
        orientation: 'landscape',
        tableData: {
          headers: ['Plan', 'Type', 'Active Count'],
          rows: (snapshotRows || []).map((row) => [
            row.plan_name,
            subscriptionTypeLabel(row.subscription_type),
            String(row.active_count ?? 0),
          ]),
        },
      });

      enqueueSnackbar('PDF exported successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error exporting PDF:', error);
      enqueueSnackbar('Failed to export PDF', { variant: 'error' });
    } finally {
      setPdfLoading(false);
    }
  };

  const handleExportCsv = async () => {
    try {
      // from/to default for Day/Week
      const now = new Date();
      const formatDate = (d: Date) => d.toISOString().slice(0, 10);
      let from: string | undefined;
      let to: string | undefined;
      const grpVal = selectedGroupBy?.value as string | undefined;
      const isDayOrWeek =
        grpVal === GroupByUnit.DAY || grpVal === GroupByUnit.WEEK;
      const isYear = grpVal === 'year';
      if (isDayOrWeek) {
        const hasCustomStart =
          tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid();
        const hasCustomEnd =
          tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid();
        const defaultFrom = moment().subtract(29, 'days').format('YYYY-MM-DD');
        const defaultTo = formatDate(now);
        from = hasCustomStart
          ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : defaultFrom;
        to = hasCustomEnd
          ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : defaultTo;
      } else if (isYear) {
        const hasCustomStart =
          tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid();
        const hasCustomEnd =
          tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid();
        const startOfYear = new Date(now.getFullYear(), 0, 1);
        const endOfYear = new Date(now.getFullYear(), 11, 31);
        const defaultFrom = formatDate(startOfYear);
        const defaultTo = formatDate(endOfYear);
        from = hasCustomStart
          ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : defaultFrom;
        to = hasCustomEnd
          ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
          : defaultTo;
      } else {
        const hasCustomStart =
          tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid();
        const hasCustomEnd =
          tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid();
        if (hasCustomStart && hasCustomEnd) {
          from = moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
          to = moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        }
      }

      const blob = await getCsv({
        groupBy: (selectedGroupBy?.value as GroupByUnit) || GroupByUnit.MONTH,
        subscription_ids: selectedSub ? [selectedSub.value] : undefined,
        subscription_types:
          selectedTypes.length > 0
            ? selectedTypes.map((o: SelectOption) => Number(o.value))
            : undefined,
        from,
        to,
        isCsv: true,
      }).unwrap();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const date = new Date().toISOString().slice(0, 10);
      link.setAttribute(
        'download',
        `historical_trends_${
          (selectedGroupBy?.value as GroupByUnit) || GroupByUnit.MONTH
        }_${date}.csv`
      );
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      enqueueSnackbar('CSV exported successfully', { variant: 'success' });
    } catch {
      console.error('Error occured in CSV generation');
      enqueueSnackbar('Failed to export CSV', { variant: 'error' });
    }
  };
  const [selectedTypes, setSelectedTypes] = useState<SelectOption[]>([]);
  const [getCsv, { isLoading: csvLoading }] =
    useLazyGetSubscriptionAdoptionCsvQuery();

  // Fetch subscriptions list for filter dropdown
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await SubscriptionsService.getSubscriptions(1, 100);
        if (!mounted) return;
        const opts: SelectOption[] = (res.subscriptions || []).map((s) => ({
          value: String(s.id),
          label: stripHtml(s.name || ''),
        }));
        setSubscriptionOptions(opts);
      } catch (error) {
        if (!mounted) return;
        const errorMessage =
          (error as { response?: { data?: { message?: string } } })?.response
            ?.data?.message || 'Failed to load subscriptions';
        enqueueSnackbar(errorMessage, { variant: 'error' });
      }
    })();
    return () => {
      mounted = false;
    };
  }, [enqueueSnackbar]);

  const filters = useMemo(() => {
    const now = new Date();
    const formatDate = (d: Date) => d.toISOString().slice(0, 10);

    let from: string | undefined;
    let to: string | undefined;
    const grpVal =
      (selectedGroupBy?.value as string | undefined) || GroupByUnit.MONTH;
    const isDayOrWeek =
      grpVal === GroupByUnit.DAY || grpVal === GroupByUnit.WEEK;
    const isYear = grpVal === 'year';
    const isMonth = grpVal === GroupByUnit.MONTH;

    const hasCustomStart =
      tempStartDate && moment(tempStartDate, 'DD/MM/YYYY').isValid();
    const hasCustomEnd =
      tempEndDate && moment(tempEndDate, 'DD/MM/YYYY').isValid();

    if (isDayOrWeek) {
      const defaultFrom = moment().subtract(29, 'days').format('YYYY-MM-DD');
      const defaultTo = formatDate(now);
      from = hasCustomStart
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultFrom;
      to = hasCustomEnd
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultTo;
    } else if (isYear) {
      const startOfYear = new Date(now.getFullYear(), 0, 1);
      const endOfYear = new Date(now.getFullYear(), 11, 31);
      const defaultFrom = formatDate(startOfYear);
      const defaultTo = formatDate(endOfYear);
      from = hasCustomStart
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultFrom;
      to = hasCustomEnd
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultTo;
    } else if (isMonth) {
      // Month grouping: default to current date to 365 days ago
      const defaultFrom = moment().subtract(365, 'days').format('YYYY-MM-DD');
      const defaultTo = formatDate(now);
      from = hasCustomStart
        ? moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultFrom;
      to = hasCustomEnd
        ? moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD')
        : defaultTo;
    } else {
      if (hasCustomStart && hasCustomEnd) {
        from = moment(tempStartDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
        to = moment(tempEndDate, 'DD/MM/YYYY').format('YYYY-MM-DD');
      }
    }

    return {
      groupBy: grpVal as GroupByUnit,
      subscription_ids: selectedSub ? [selectedSub.value] : undefined,
      subscription_types:
        selectedTypes.length > 0
          ? selectedTypes.map((o: SelectOption) => Number(o.value))
          : undefined,
      from,
      to,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    };
  }, [
    selectedGroupBy,
    selectedSub,
    selectedTypes,
    tempStartDate,
    tempEndDate,
    pagination.pageIndex,
    pagination.pageSize,
  ]);

  const { data, isLoading } = useGetSubscriptionAdoptionReportQuery(filters);

  // Reset to first page when filters (other than page/limit) change
  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [selectedGroupBy, selectedSub, selectedTypes, tempStartDate, tempEndDate]);

  const handleDateRangeChange = (startDate: string, endDate: string) => {
    setTempStartDate(startDate);
    setTempEndDate(endDate);
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const resetFilters = () => {
    setSelectedGroupBy(groupByOptions[2]);
    setSelectedSub(null);
    setSelectedTypes([]);
    setTempStartDate('');
    setTempEndDate('');
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  };

  const snapshotRows: SubscriptionActiveSnapshotItem[] = useMemo(
    () =>
      (data?.snapshot || []).map((s) => ({
        ...s,
        plan_name: stripHtml(s.plan_name),
      })),
    [data?.snapshot]
  );

  const historyPoints = useMemo(() => {
    const history = (data?.history || []).map((h) => ({
      ...h,
      plan_name: stripHtml(h.plan_name),
    })) as SubscriptionHistoryItem[];

    // Aggregate net and cancellations across all plans per period_start
    const netByPeriod = new Map<string, number>();
    const cancelByPeriod = new Map<string, number>();
    history.forEach((h) => {
      // Prefer period_start from the API; fall back to period if needed\
      const rawDate = h.period_start || h.period;
      if (!rawDate) return;
      const d = new Date(rawDate);
      if (Number.isNaN(d.getTime())) return;
      const key = d.toISOString().slice(0, 10);
      netByPeriod.set(key, (netByPeriod.get(key) || 0) + (h.net ?? 0));
      cancelByPeriod.set(
        key,
        (cancelByPeriod.get(key) || 0) + (h.cancellations ?? 0)
      );
    });

    const sortedKeys = Array.from(netByPeriod.keys()).sort();
    const categories = sortedKeys.map((k) => formatDisplayDate(new Date(k)));
    const netSeries = sortedKeys.map((k) => netByPeriod.get(k) || 0);
    const cancelSeries = sortedKeys.map((k) => cancelByPeriod.get(k) || 0);

    const options: ApexOptions = {
      series: [
        { name: 'Net New Subscriptions', data: netSeries },
        { name: 'Cancellations', data: cancelSeries },
      ],
      chart: {
        height: 300,
        type: 'area',
        toolbar: { show: false },
      },
      dataLabels: { enabled: false },
      legend: { show: true },
      stroke: { curve: 'smooth', show: true, width: 3 },
      xaxis: {
        categories,
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: 'var(--tw-gray-700)', fontSize: '12px' } },
      },
      yaxis: {
        labels: { style: { colors: 'var(--tw-gray-700)', fontSize: '12px' } },
      },
      markers: {
        size: 0,
        colors: 'var(--tw-primary-light)',
        strokeColors: 'var(--tw-primary)',
        strokeWidth: 4,
        hover: { size: 6, sizeOffset: 0 },
      },
      fill: { gradient: { opacityFrom: 0.25, opacityTo: 0 } },
      grid: {
        borderColor: 'var(--tw-gray-200)',
        strokeDashArray: 5,
        yaxis: { lines: { show: true } },
        xaxis: { lines: { show: false } },
      },
    };

    return { options };
  }, [data?.history]);

  const columns = [
    {
      accessorFn: (row: SubscriptionActiveSnapshotItem) => row.plan_name,
      id: 'plan_name',
      header: 'Plan',
      enableSorting: false,
    },
    {
      accessorFn: (row: SubscriptionActiveSnapshotItem) =>
        row.subscription_type,
      id: 'type',
      header: 'Customer Segment',
      enableSorting: false,
      cell: ({
        row,
      }: {
        row: { original: SubscriptionActiveSnapshotItem };
      }) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
          {subscriptionTypeLabel(row.original.subscription_type)}
        </span>
      ),
    },
    {
      accessorFn: (row: SubscriptionActiveSnapshotItem) => row.active_count,
      id: 'active',
      header: 'Active Count',
      enableSorting: false,
    },
  ];

  return (
    <Container className="historical-trends">
      {hasPermission('subscription_management_read') && (
        <>
          <div className="flex items-start justify-between mb-6">
            <h1
              id="historical-trends-heading"
              className="text-xl font-semibold"
              tabIndex={-1}
            >
              Historical Growth Trends
            </h1>
            <div className="flex flex-wrap gap-3 sm:gap-5 justify-start sm:justify-end">
              <div className="min-w-[160px]">
                <Select
                  classNamePrefix="react-select"
                  placeholder="Group by"
                  value={selectedGroupBy}
                  options={groupByOptions}
                  onChange={(val) => setSelectedGroupBy(val as SelectOption)}
                  aria-label="Group by"
                />
              </div>
              <div className="min-w-[220px]">
                <Select
                  classNamePrefix="react-select"
                  placeholder="Filter subscription"
                  options={subscriptionOptions}
                  value={selectedSub}
                  isClearable
                  onChange={(val) => setSelectedSub(val as SelectOption | null)}
                  aria-label="Filter subscription"
                />
              </div>
              <div className="min-w-[220px]">
                <Select
                  isMulti
                  classNamePrefix="react-select"
                  placeholder="Filter Customer segment"
                  options={subscriptionTypeOptions}
                  value={selectedTypes}
                  isClearable
                  onChange={(vals: MultiValue<SelectOption>) =>
                    setSelectedTypes(vals as SelectOption[])
                  }
                  aria-label="Filter Customer segment"
                />
              </div>
              <div className="flex-shrink-0">
                <DateRangePicker
                  startDateName="from"
                  endDateName="to"
                  isMandatory={false}
                  value={[tempStartDate, tempEndDate]}
                  onChange={([newStartDate, newEndDate]) => {
                    handleDateRangeChange(
                      newStartDate as string,
                      newEndDate as string
                    );
                  }}
                  allowPastDates={true}
                  allowFutureDates={false}
                  className="w-full sm:w-64 h-8 sm:h-10 text-sm"
                />
              </div>
              {(selectedGroupBy?.value !== GroupByUnit.MONTH ||
                !!selectedSub ||
                selectedTypes.length > 0 ||
                !!tempStartDate ||
                !!tempEndDate) && (
                <div className="flex items-center">
                  <button
                    type="button"
                    className="btn btn-sm btn-light flex items-center gap-2"
                    onClick={resetFilters}
                    title="Clear filters"
                  >
                    Clear Filters
                  </button>
                </div>
              )}
              <div className="flex items-center gap-2 flex-shrink-0 whitespace-nowrap">
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleExportPdf}
                  disabled={pdfLoading}
                >
                  {pdfLoading ? 'Exporting...' : 'Export PDF'}
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={handleExportCsv}
                  disabled={csvLoading}
                >
                  {csvLoading ? 'Exporting...' : 'Export CSV'}
                </button>
              </div>
            </div>
          </div>

          <div className="card mb-6">
            <div
              className="card-body flex flex-col justify-end items-stretch grow px-3 py-1"
              ref={chartRef}
            >
              <ApexChart
                id="historical_trends_chart"
                options={historyPoints.options}
                series={historyPoints.options.series}
                type="area"
                width="100%"
                height="300"
              />
            </div>
          </div>

          <div className="card">
            <div className="card-body" ref={tableRef}>
              <DataGrid
                columns={columns}
                data={snapshotRows}
                rowSelect={false}
                loading={isLoading}
                pagination={{
                  page: pagination.pageIndex,
                  size: pagination.pageSize,
                  sizes: [10, 25, 50, 100],
                  sizesLabel: 'Show',
                  sizesDescription: 'per page',
                  more: false,
                  info: '{from} - {to} of {count}',
                  totalItems:
                    data?.snapshotPagination?.totalItems ?? snapshotRows.length,
                  totalPages:
                    data?.snapshotPagination?.totalPages ??
                    Math.max(
                      1,
                      Math.ceil(
                        (snapshotRows.length || 0) / (pagination.pageSize || 1)
                      )
                    ),
                  setPagination: setPagination,
                }}
                serverSide={!!data?.snapshotPagination}
                onFilterReset={resetFilters}
                hasActiveFilters={
                  selectedGroupBy?.value !== GroupByUnit.MONTH ||
                  !!selectedSub ||
                  selectedTypes.length > 0 ||
                  !!tempStartDate ||
                  !!tempEndDate
                }
                aria-label="Active subscriptions snapshot table"
              />
            </div>
          </div>
        </>
      )}
    </Container>
  );
};

export default HistoricalTrends;
